import React from 'react';
import BaseModal from '../../../components/Portal/BaseModal';
import {
  ModalHeader, CloseButton, ModalBody, ModalFooter,
  ScopeOption, ChangeSummary, SecondaryButton, ErrorState
} from './Timetable.styles';

/**
 * After a drag: does the change apply to this one date, or to every week of the term?
 *
 * The same question Google Calendar asks ("This event" / "All events"), because the two
 * answers write to different places — one week becomes a LessonPortal override, the whole
 * term rewrites that schedule on the course group.
 */

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const describe = (pending) => {
  const { item, change, groupBy } = pending;
  const lines = [];
  if (change.dayIndex !== null && change.dayIndex !== undefined) {
    lines.push(<div key="day">Day: <b>{DAY_NAMES[(item.dayOfWeek || 1) - 1]}</b> → <b>{DAY_NAMES[change.dayIndex]}</b></div>);
  }
  if (change.startTime !== item.startTime || change.endTime !== item.endTime) {
    lines.push(<div key="time">Time: <b>{item.startTime}–{item.endTime}</b> → <b>{change.startTime}–{change.endTime}</b></div>);
  }
  if (change.columnKey !== undefined) {
    const label = groupBy === 'teacher' ? 'Teacher' : 'Room';
    const from = groupBy === 'teacher'
      ? (item.teachers || []).map(t => t.name).join(', ') || '—'
      : item.room || '—';
    lines.push(<div key="col">{label}: <b>{from}</b> → <b>{change.columnName}</b></div>);
  }
  return lines;
};

const ScopeModal = ({ pending, saving, error, onChoose, onClose }) => {
  const { item, change } = pending;
  const dayChanged = change.dayIndex !== null && change.dayIndex !== undefined;
  // This week was already changed on its own. "Whole term" then means the exception has said
  // its piece and goes, so the answer has to say so before it is acted on.
  const overridden = Boolean(item.lessonId);

  return (
    <BaseModal maxWidth="480px">
      <ModalHeader>
        <h2>Apply change to…</h2>
        <CloseButton onClick={onClose} aria-label="Close" disabled={saving}>×</CloseButton>
      </ModalHeader>

      <ModalBody>
        {error && <ErrorState>{error}</ErrorState>}

        <ChangeSummary>
          <div><b>{item.title}</b> · {item.date}</div>
          {describe(pending)}
        </ChangeSummary>

        <ScopeOption $primary onClick={() => onChoose('week')} disabled={saving}>
          <strong>This week only</strong>
          <span>
            Only the class on {item.date} changes. Every other week stays as scheduled.
            {overridden ? ' This week already has its own change; this replaces it.' : ''}
          </span>
        </ScopeOption>

        <ScopeOption onClick={() => onChoose('term')} disabled={saving}>
          <strong>Whole term</strong>
          <span>
            Changes the course group's weekly schedule from now on
            {dayChanged ? ' — every week moves to the new day' : ''}.
            Past weeks are not rewritten.
            {overridden ? ' The separate change made to this week is cleared.' : ''}
          </span>
        </ScopeOption>
      </ModalBody>

      <ModalFooter>
        <SecondaryButton onClick={onClose} disabled={saving}>
          {saving ? 'Saving…' : 'Cancel'}
        </SecondaryButton>
      </ModalFooter>
    </BaseModal>
  );
};

export default ScopeModal;
